/**
 * SchemaEditor Component
 * 
 * Visual editor for creating/editing JSON schemas.
 * Features:
 * - Add/remove properties
 * - Set type, description, default value
 * - Mark required fields
 * - Set enum values for string/number types
 * - Define array item types
 * - Nested object support (basic)
 */

import React, { useState } from 'react';
import { JSONSchema, JSONSchemaProperty } from '@simcheck/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/contexts/ToastContext';

interface SchemaEditorProps {
  value?: JSONSchema;
  onChange: (schema: JSONSchema) => void;
  label?: string;
  placeholder?: string;
}

interface PropertyFormData {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
  defaultValue: string;
  required: boolean;
  enumValues: string;
  arrayItemType?: 'string' | 'number' | 'boolean' | 'object';
}

const EMPTY_PROPERTY: PropertyFormData = {
  name: '',
  type: 'string',
  description: '',
  defaultValue: '',
  required: false,
  enumValues: '',
};

export const SchemaEditor: React.FC<SchemaEditorProps> = ({
  value,
  onChange,
  label = 'Schema',
  placeholder = 'No properties defined',
}) => {
  const toast = useToast();
  const [properties, setProperties] = useState<Record<string, JSONSchemaProperty>>(
    value?.properties || {}
  );
  const [requiredFields, setRequiredFields] = useState<string[]>(value?.required || []);
  const [editingProperty, setEditingProperty] = useState<PropertyFormData | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; key: string | null }>({
    isOpen: false,
    key: null
  });

  // Update parent when schema changes
  const updateSchema = (
    newProperties: Record<string, JSONSchemaProperty>,
    newRequired: string[]
  ) => {
    setProperties(newProperties);
    setRequiredFields(newRequired);
    onChange({
      type: 'object',
      properties: newProperties,
      required: newRequired.length > 0 ? newRequired : undefined,
      additionalProperties: false,
    });
  };

  // Start adding a new property
  const startAddProperty = () => {
    setEditingProperty({ ...EMPTY_PROPERTY });
    setEditingKey(null);
  };

  // Start editing existing property
  const startEditProperty = (key: string) => {
    const prop = properties[key];
    // Legacy editor UI only supports 5 primitive types; fold 'integer' back to
    // 'number' so the select renders without a TS error. (Catalogue primitives
    // round-trip 'integer' natively via the seed; this editor is for legacy
    // super-admin primitive CRUD only.)
    const uiType = prop.type === 'integer' ? 'number' : prop.type;
    setEditingProperty({
      name: key,
      type: uiType,
      description: prop.description || '',
      defaultValue: prop.default !== undefined ? String(prop.default) : '',
      required: requiredFields.includes(key),
      enumValues: prop.enum ? prop.enum.join(', ') : '',
      arrayItemType: prop.items?.type as any,
    });
    setEditingKey(key);
  };

  // Save property
  const saveProperty = () => {
    if (!editingProperty || !editingProperty.name.trim()) {
      toast.error('Property name is required');
      return;
    }

    const propertyName = editingProperty.name.trim();

    // Build the property object
    const newProperty: JSONSchemaProperty = {
      type: editingProperty.type,
      description: editingProperty.description || undefined,
    };

    // Add default value
    if (editingProperty.defaultValue.trim()) {
      try {
        if (editingProperty.type === 'number') {
          newProperty.default = parseFloat(editingProperty.defaultValue);
        } else if (editingProperty.type === 'boolean') {
          newProperty.default = editingProperty.defaultValue.toLowerCase() === 'true';
        } else {
          newProperty.default = editingProperty.defaultValue;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Add enum values
    if (editingProperty.enumValues.trim() && (editingProperty.type === 'string' || editingProperty.type === 'number')) {
      const enumValues = editingProperty.enumValues.split(',').map((v) => v.trim()).filter(Boolean);
      if (enumValues.length > 0) {
        newProperty.enum = editingProperty.type === 'number' 
          ? enumValues.map(v => parseFloat(v))
          : enumValues;
      }
    }

    // Add array item type
    if (editingProperty.type === 'array' && editingProperty.arrayItemType) {
      newProperty.items = {
        type: editingProperty.arrayItemType,
      };
    }

    // Update properties
    const newProperties = { ...properties };
    if (editingKey && editingKey !== propertyName) {
      // Renaming - remove old key
      delete newProperties[editingKey];
    }
    newProperties[propertyName] = newProperty;

    // Update required fields
    let newRequired = [...requiredFields];
    if (editingKey && editingKey !== propertyName) {
      // Remove old name from required
      newRequired = newRequired.filter((f) => f !== editingKey);
    }
    if (editingProperty.required) {
      if (!newRequired.includes(propertyName)) {
        newRequired.push(propertyName);
      }
    } else {
      newRequired = newRequired.filter((f) => f !== propertyName);
    }

    updateSchema(newProperties, newRequired);
    setEditingProperty(null);
    setEditingKey(null);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingProperty(null);
    setEditingKey(null);
  };

  // Delete property
  const deleteProperty = (key: string) => {
    setDeleteConfirm({ isOpen: true, key });
  };

  const confirmDelete = () => {
    if (!deleteConfirm.key) return;

    const newProperties = { ...properties };
    delete newProperties[deleteConfirm.key];

    const newRequired = requiredFields.filter((f) => f !== deleteConfirm.key);

    updateSchema(newProperties, newRequired);
    setDeleteConfirm({ isOpen: false, key: null });
  };

  const propertyKeys = Object.keys(properties);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-foreground">
          {label}
        </label>
        <button
          type="button"
          onClick={startAddProperty}
          className="px-3 py-1.5 text-sm font-medium text-accent-blue hover:bg-accent-blue/10 dark:hover:bg-accent-blue/20 border border-accent-blue/30 rounded transition-colors"
        >
          + Add Property
        </button>
      </div>

      {/* Properties List */}
      {propertyKeys.length === 0 && !editingProperty ? (
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-muted-foreground/70 italic">
            {placeholder}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {propertyKeys.map((key) => {
            const prop = properties[key];
            const isRequired = requiredFields.includes(key);

            return (
              <div
                key={key}
                className="bg-card border border-border rounded-lg p-4 flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-sm font-mono font-semibold text-foreground">
                      {key}
                    </code>
                    <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-accent-blue/10 dark:bg-accent-blue/20 text-accent-blue">
                      {prop.type}
                    </span>
                    {isRequired && (
                      <span className="text-xs font-semibold px-1.5 py-0.5 bg-status-error-bg text-status-error rounded">
                        required
                      </span>
                    )}
                  </div>
                  {prop.description && (
                    <p className="text-xs text-muted-foreground">
                      {prop.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startEditProperty(key)}
                    className="p-1.5 text-muted-foreground hover:text-accent-blue hover:bg-muted rounded transition-colors"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteProperty(key)}
                    className="p-1.5 text-muted-foreground hover:text-status-error hover:bg-muted rounded transition-colors"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Property Editor Form */}
      {editingProperty && (
        <div className="bg-accent-blue/10 dark:bg-accent-blue/20 border-2 border-accent-blue/30 rounded-lg p-4 space-y-4">
          <h4 className="text-sm font-semibold text-foreground">
            {editingKey ? 'Edit Property' : 'Add Property'}
          </h4>

          {/* Property Name */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Property Name *
            </label>
            <input
              type="text"
              value={editingProperty.name}
              onChange={(e) => setEditingProperty({ ...editingProperty, name: e.target.value })}
              placeholder="e.g., phoneNumber, signalStrength"
              className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Type *
            </label>
            <select
              value={editingProperty.type}
              onChange={(e) => setEditingProperty({ ...editingProperty, type: e.target.value as any })}
              className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue"
            >
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="array">Array</option>
              <option value="object">Object</option>
            </select>
          </div>

          {/* Array Item Type (only for array) */}
          {editingProperty.type === 'array' && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Array Item Type
              </label>
              <select
                value={editingProperty.arrayItemType || 'string'}
                onChange={(e) => setEditingProperty({ ...editingProperty, arrayItemType: e.target.value as any })}
                className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="object">Object</option>
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Description
            </label>
            <input
              type="text"
              value={editingProperty.description}
              onChange={(e) => setEditingProperty({ ...editingProperty, description: e.target.value })}
              placeholder="Brief description of this property"
              className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          {/* Default Value */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Default Value
            </label>
            <input
              type="text"
              value={editingProperty.defaultValue}
              onChange={(e) => setEditingProperty({ ...editingProperty, defaultValue: e.target.value })}
              placeholder={editingProperty.type === 'boolean' ? 'true or false' : 'Default value'}
              className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue"
            />
          </div>

          {/* Enum Values (only for string/number) */}
          {(editingProperty.type === 'string' || editingProperty.type === 'number') && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Enum Values (comma-separated, optional)
              </label>
              <input
                type="text"
                value={editingProperty.enumValues}
                onChange={(e) => setEditingProperty({ ...editingProperty, enumValues: e.target.value })}
                placeholder="e.g., low, medium, high"
                className="block w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
            </div>
          )}

          {/* Required Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="required-checkbox"
              checked={editingProperty.required}
              onChange={(e) => setEditingProperty({ ...editingProperty, required: e.target.checked })}
              className="w-4 h-4 text-accent-blue border-border rounded focus:ring-2 focus:ring-accent-blue"
            />
            <label htmlFor="required-checkbox" className="text-sm text-foreground">
              Required field
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={saveProperty}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded transition-colors"
            >
              {editingKey ? 'Update' : 'Add'}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted border border-border rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.isOpen} onOpenChange={(open) => !open && setDeleteConfirm({ isOpen: false, key: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the property "{deleteConfirm.key}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-status-error hover:bg-status-error/90 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SchemaEditor;


